import React, { useState, useEffect, useRef } from "react";
import { DocumentTextIcon, UploadIcon } from "asset/icons";
import UploadDocModal from "./uploadDocModal";
import { handleUploadFileUtils } from "../../api/fetchers";
import { Circles } from "react-loader-spinner";
import { Trash } from "iconsax-reactjs";

export default function FileUploadTile({
    label = "Click to Upload",
    sublabel = "JPEG, PNG, JPG, PDF • 5 MB max.",
    fileId,
    onUploaded,
    onCleared,
    disabled = false,
    initialName = "",
    directUpload = false,
    labelClassName = "text-[14px] font-[400]",
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadedName, setUploadedName] = useState(initialName);
    const [uploadedSize, setUploadedSize] = useState("");
    const [pendingFile, setPendingFile] = useState(null);
    const fileInputRef = useRef(null);
    const isDirectUpload = directUpload || label === "Click to Upload";
    const acceptedFileTypes = "image/jpeg,image/png,image/jpg,application/pdf";

    useEffect(() => {
        setUploadedName(initialName);
        setUploadedSize("");
        setPendingFile(null);
    }, [initialName]);

    const formatFileSize = (sizeInBytes) => {
        if (!sizeInBytes) return "";

        return `${Math.max(1, Math.round(sizeInBytes / (1024 * 1024)))} MB`;
    };

    const getUploadedUrl = (raw) =>
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.url?.link ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.url?.link ||
        raw?.data?.link ||
        "";

    const handleUpload = async (file, name, id) => {
        try {
            setLoading(true);
            setPendingFile(file);
            const raw = await handleUploadFileUtils({ selectedFile: file, name });
            const url = getUploadedUrl(raw);
            if (!url) {
                throw new Error(raw?.message || "No upload URL returned");
            }
            setUploadedName(name);
            setUploadedSize(formatFileSize(file?.size));
            await onUploaded?.({ name, url, fileId: id });
            setOpen(false);
        } catch (e) {
            console.error("Upload failed:", e);
        } finally {
            setLoading(false);
            setPendingFile(null);
        }
    };

    const handleDirectFileSelect = async (event) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) return;

        await handleUpload(selectedFile, selectedFile.name, fileId);
        event.target.value = "";
    };

    const handleClearUploadedFile = (event) => {
        event.stopPropagation();
        setUploadedName("");
        setUploadedSize("");
        setPendingFile(null);
        onCleared?.();
    };

    return (
        <>
            <button
                type="button"
                disabled={disabled || loading}
                onClick={() => {
                    if (disabled || loading) return;

                    if (isDirectUpload) {
                        fileInputRef.current?.click();
                        return;
                    }

                    setOpen(true);
                }}
                className={`w-full text-left disabled:cursor-not-allowed disabled:opacity-60 ${
                    (uploadedName || loading) && isDirectUpload
                        ? "bg-[#f1f5f9] rounded-[8px] !px-4 !py-5 flex items-center"
                        : "rounded-2xl bg-[#f1f5f9] !px-4 !py-4 flex items-center gap-4 hover:bg-[#E8EEF6]"
                }`}
            >
                {(uploadedName || loading) && isDirectUpload ? (
                    <>
                        <div className="size-[40px] rounded-full bg-[#DCCBFF] flex items-center justify-center shrink-0">
                            {loading ? (
                                <Circles height="20" width="20" color="#6F00FF" />
                            ) : (
                                <DocumentTextIcon className="size-5" />
                            )}
                        </div>
                        <span className="min-w-0 ml-4 mr-auto">
                            <span className="block aku-body-small-regular text-main-dark truncate">
                                {loading ? pendingFile?.name || "Uploading..." : uploadedName}
                            </span>
                            <span className="block aku-label-regular text-[#7A80AE]">
                                {loading
                                    ? `Uploading... ${formatFileSize(pendingFile?.size)}`
                                    : uploadedSize}
                            </span>
                        </span>
                        {!loading && !disabled && (
                            <button
                                type="button"
                                className="shrink-0 text-[#6F739B]"
                                onClick={handleClearUploadedFile}
                                aria-label="Delete uploaded document"
                            >
                                <Trash className="size-5" />
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <UploadIcon className="size-8" />
                        <span className="flex-1">
                            {uploadedName ? (
                                <span className="block text-[#161827] text-[14px] font-[500] truncate">
                                    {uploadedName}
                                </span>
                            ) : (
                                <>
                                    <span className={`${labelClassName} block text-[#161827]`}>
                                        {label}
                                    </span>
                                    <span className="block text-[12px] text-[#9396B4]">
                                        {sublabel}
                                    </span>
                                </>
                            )}
                        </span>
                    </>
                )}
            </button>

            {isDirectUpload && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedFileTypes}
                    className="hidden"
                    onChange={handleDirectFileSelect}
                    disabled={disabled || loading}
                />
            )}

            {!isDirectUpload && open && (
                <UploadDocModal
                    close={() => setOpen(false)}
                    uploadFunc={handleUpload}
                    loading={loading}
                    fileId={fileId}
                />
            )}
        </>
    );
}
